import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const testimonials = [
  {
    name: "Allan Asplin",
    company: "Judy Lindsay Realty",
    url: "https://www.judylindsay.com",
    image: "https://i.ibb.co/R431GNJ8/Screenshot-2025-04-16-163305.png",
    text: "Joel set up a chatbot for our team at Judy Lindsay Realty, and it's been a solid addition to how we handle online inquiries. It helps capture leads, answers common questions, and even recommends property listings—all without us having to lift a finger. He was easy to work with, quick to respond, and made sure everything ran smoothly. Great experience",
    fontClass: "font-gotham"
  },
  {
    name: "Lihu Eliyahu",
    company: "Slot Secured",
    url: "https://deluxe-zuccutto-dd73e3.netlify.app",
    image: "https://i.ibb.co/GfGpvxyj/Screenshot-2025-03-06-145616.png",
    text: "Working with Joel was seamless from start to finish. He built a custom app and site for Slot Secured that looks great and functions exactly how we envisioned—our clients can now book appointments and access subscription features effortlessly. He handled both the frontend and backend like a pro and made the whole process easy on our end. Would definitely recommend him if you're looking for someone reliable who actually delivers",
    fontClass: "font-orbitron"
  },
  {
    name: "Kaya Suffield",
    company: "Deeply Rooted Expansion",
    url: "https://deeplyrootedexpansion.com",
    image: "https://i.ibb.co/bjzLs03S/DRE.png",
    text: "As someone who is just starting up their business, it's important to me that my potential clients can grasp who I am and what it is that I do. Joel did such a great job of portraying my business and myself through the build and aesthetic of my website. I feel confident that when my future clients are looking into working with me that they will get a clear feel for what it is that I offer. The website feels very true to who I am and I'm very pleased with how it turned out. If you're thinking of building a website but don't quite know where to start, Joel is extremely knowledgeable and a great person to work with.",
    fontClass: "font-garamond"
  }
];

export default function TestimonialsSection() {
  const { theme } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const containerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const nextTestimonial = () => {
    setDirection(1);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  const previousTestimonial = () => {
    setDirection(-1);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const swipeDistance = touchStartX.current - touchEndX.current;

    if (Math.abs(swipeDistance) > 50) {
      if (swipeDistance > 0) {
        nextTestimonial();
      } else {
        previousTestimonial();
      }
    }
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: [0.4, 0.0, 0.2, 1],
      },
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      transition: {
        duration: 0.4,
        ease: [0.4, 0.0, 0.2, 1],
      },
    }),
  };

  return (
    <section className={`py-20 px-4 ${theme === 'dark' ? 'bg-black' : 'bg-white'}`}>
      <div className="max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl md:text-4xl font-bold text-center"
        >
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
            What Our Clients Say
          </span>
        </motion.h2>

        <div 
          className="relative px-4 sm:px-0 mt-16" 
          ref={containerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="flex justify-center items-center min-h-[600px]">
            <AnimatePresence initial={false} custom={direction} mode="popLayout">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className={`w-full max-w-[400px] ${
                  theme === 'dark' ? 'bg-gray-900' : 'bg-white shadow-xl'
                } rounded-2xl p-6`}
                style={{ touchAction: 'pan-y' }}
              >
                <div className="flex flex-col items-center text-center">
                  <motion.div 
                    className="mb-6"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  >
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-blue-500 shadow-lg">
                      <img
                        src={testimonials[currentIndex].image}
                        alt={`${testimonials[currentIndex].company} website`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </motion.div>
                  <div className="space-y-4">
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.3 }}
                      className={`text-base leading-relaxed ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                      }`}
                    >
                      "{testimonials[currentIndex].text}"
                    </motion.p>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.3 }}
                      className="pt-4"
                    >
                      <a
                        href={testimonials[currentIndex].url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-lg font-semibold block hover:underline ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        {testimonials[currentIndex].name}
                      </a>
                      <a
                        href={testimonials[currentIndex].url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-blue-500 hover:underline block mt-1 ${testimonials[currentIndex].fontClass}`}
                      >
                        {testimonials[currentIndex].company}
                      </a>
                    </motion.div>
                  </div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                  className="mt-6 flex justify-center items-center gap-4"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      previousTestimonial();
                    }}
                    className={`p-2 rounded-full transition-colors ${
                      theme === 'dark'
                        ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                    }`}
                    aria-label="Previous testimonial"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <div className="flex gap-2">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDirection(index > currentIndex ? 1 : -1);
                          setCurrentIndex(index);
                        }}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentIndex
                            ? 'bg-blue-500'
                            : theme === 'dark'
                            ? 'bg-gray-700 hover:bg-gray-600'
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                        aria-label={`Go to testimonial ${index + 1}`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      nextTestimonial();
                    }}
                    className={`p-2 rounded-full transition-colors ${
                      theme === 'dark'
                        ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                    }`}
                    aria-label="Next testimonial"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}